<article class="content">
  <h1>Terms of Service</h1>

  <p>
    Welcome to {{appname}}. These Terms of Service explain the rules and
    conditions for using our website, creating an account, placing an order, and
    purchasing products from us.
  </p>

  <p>
    By accessing or using our website, you agree to be bound by these Terms of
    Service. If you do not agree with these terms, please do not use our
    website.
  </p>

  <p>
    <em>Effective Date: Feb 2nd, 2023</em>
  </p>

  <h2>1. About {{appname}}</h2>

  <p>
    {{appname}} is an online fashion brand offering women’s clothing and related
    products. Throughout these Terms, “{{appname}},” “we,” “us,” and “our” refer
    to {{appname}}, and “you” or “your” refers to the customer or website user.
  </p>

  <h2>2. Use of Our Website</h2>

  <p>
    You may use our website only for lawful purposes and in accordance with
    these Terms. You agree not to misuse our website, interfere with its
    security, attempt unauthorized access, or use the website in a way that may
    harm {{appname}}, other users, or third parties.
  </p>

  <h2>3. Account Registration</h2>

  <p>
    Our website may require you to create an account or log in using your email
    address or Google account to access certain features, place orders, or
    manage your information.
  </p>

  <p>
    You are responsible for maintaining the confidentiality of your account
    login information and for all activity that occurs under your account.
    Please contact us immediately if you believe your account has been accessed
    without authorization.
  </p>

  <h2>4. Product Information</h2>

  <p>
    We do our best to display product descriptions, colors, sizes, images,
    pricing, and availability as accurately as possible. However, we cannot
    guarantee that all product information will always be complete, accurate,
    current, or error-free.
  </p>

  <p>
    Product colors may vary slightly depending on your device screen, lighting,
    photography, and display settings.
  </p>

  <h2>5. Orders and Payments</h2>

  <p>
    By placing an order on our website, you agree that all information you
    provide is accurate, complete, and current.
  </p>

  <p>
    All orders are subject to acceptance and availability. We reserve the right
    to refuse, cancel, or limit any order for any reason, including suspected
    fraud, pricing errors, product unavailability, incorrect product
    information, or violation of these Terms.
  </p>

  <p>
    Payment must be completed at checkout through the available payment methods
    on our website. Prices are listed in the currency displayed at checkout and
    may be subject to applicable taxes, shipping fees, or other charges.
  </p>

  <h2>6. Shipping and Delivery</h2>

  <p>
    Shipping times and delivery estimates are provided for convenience only and
    are not guaranteed. Delivery may be affected by carrier delays, customs
    processing, weather, holidays, incorrect shipping information, or other
    circumstances beyond our control.
  </p>

  <p>
    You are responsible for providing a correct and complete shipping address.
    {{appname}} is not responsible for delays, failed deliveries, or lost
    packages caused by incorrect or incomplete address information provided by
    the customer.
  </p>

  <h2>7. Returns and Exchanges</h2>

  <p>
    Returns and exchanges are governed by our Return &amp; Exchange Policy. We
    offer free exchanges within 30 days of delivery for eligible clothing items
    that are unworn, unwashed, unused, and in resellable condition.
  </p>

  <p>
    If your item arrives damaged, defective, incorrect, or has an issue caused
    by us, please contact us. Once the issue is confirmed, we will provide a
    refund directly — no return required.
  </p>

  <p>
    Final Sale items cannot be returned or exchanged, unless the item arrives
    damaged, defective, incorrect, or has an issue caused by us.
  </p>

  <h2>8. Promotions and Discounts</h2>

  <p>
    Promotions, discounts, coupon codes, and special offers may be subject to
    additional terms, expiration dates, exclusions, or limitations. We reserve
    the right to modify, suspend, or cancel promotions at any time without prior
    notice.
  </p>

  <p>
    Discount codes cannot be applied retroactively to previous orders unless
    otherwise stated.
  </p>

  <h2>9. Intellectual Property</h2>

  <p>
    All content on our website, including text, images, graphics, logos, product
    photos, designs, layout, icons, and brand elements, is owned by or licensed
    to {{appname}} and is protected by applicable intellectual property laws.
  </p>

  <p>
    You may not copy, reproduce, distribute, modify, display, sell, or use any
    website content for commercial purposes without our prior written
    permission.
  </p>

  <h2>10. User Content</h2>

  <p>
    If you submit reviews, photos, comments, feedback, or other content to us,
    you represent that you have the right to share such content and that it does
    not violate the rights of any third party.
  </p>

  <p>
    By submitting content to {{appname}}, you grant us permission to use,
    display, reproduce, edit, and share that content for website, marketing,
    social media, or customer service purposes, unless prohibited by applicable
    law.
  </p>

  <h2>11. Privacy</h2>

  <p>
    Your use of our website is also subject to our Privacy Policy, which
    explains how we collect, use, and protect your personal information.
  </p>

  <h2>12. Third-Party Services</h2>

  <p>
    Our website may use or link to third-party services, including payment
    processors, shipping carriers, analytics providers, social media platforms,
    or Google login services. We are not responsible for the content, policies,
    or practices of third-party websites or services.
  </p>

  <h2>13. Disclaimer of Warranties</h2>

  <p>
    Our website and products are provided on an “as is” and “as available”
    basis. To the fullest extent permitted by law, {{appname}} makes no
    warranties, express or implied, regarding the website, product availability,
    uninterrupted access, error-free operation, or fitness for a particular
    purpose.
  </p>

  <h2>14. Limitation of Liability</h2>

  <p>
    To the fullest extent permitted by law, {{appname}} shall not be liable for
    any indirect, incidental, special, consequential, or punitive damages
    arising from your use of our website, purchase of products, delay in
    delivery, inability to access the website, or reliance on any information
    provided on the website.
  </p>

  <p>
    Nothing in these Terms limits any rights you may have under applicable
    consumer protection laws.
  </p>

  <h2>15. Indemnification</h2>

  <p>
    You agree to indemnify and hold harmless {{appname}} from any claims,
    damages, losses, liabilities, costs, or expenses arising from your misuse of
    our website, violation of these Terms, or violation of any rights of another
    person or entity.
  </p>

  <h2>16. Changes to These Terms</h2>

  <p>
    We may update or modify these Terms of Service from time to time. Any
    changes will be posted on this page with an updated effective date. Your
    continued use of our website after changes are posted means you accept the
    updated Terms.
  </p>

  <h2>17. Contact Us</h2>

  <p>
    If you have any questions about these Terms of Service, please contact us
    at:
  </p>

  <p>
    <strong>Email:</strong>
    <a href="mailto:{{email}}" target="_blank">{{email}}</a>
  </p>

  <p>
    <strong>Mailing Address:</strong><br />
    {{address1}}<br />
    {{address2}}<br />
    United States
  </p>
</article>
